import React, { useState, useEffect, useRef } from 'react'
import { gql } from '@apollo/client'
import { useOfflineQuery } from '../../../Hooks/graphql_utils'

import { CardWithBreadcrumb, Table, Spinner, Card, ButtonLink, ErrorMessage, Row, Col } from '../../../Elements'
import { useNavigate } from 'react-router-dom'
import { NonIdealState, Icon } from '@blueprintjs/core'
import JobFilters from '../JobFilters'
import _ from 'lodash'
import { useMediaQuery } from 'react-responsive'
import { useJobSearchCriteria } from '../../../Hooks/jobSearchCriteria'
import { JOB_SEARCH_FRAGMENT } from '../../../utils/queries'
import { useOnlineUtils } from '../../../Hooks/offline'

const QUERY = gql`
  ${JOB_SEARCH_FRAGMENT}
  query jobSearchQuery ($searchCriteria: JobSearchCriteriaInput) {
    jobs(searchCriteria: $searchCriteria) {
      ...JobSearchFragment
    }
    customers: lookupCustomer {
      id
      company_name
      is_active
    }
    homeowners {
      id
      first_name 
      last_name
    }
    customer_contacts {
      id
      first_name
      last_name
    }

    lookupJobCategory {
      id
      name
    }
    lookupPebbleColor {
      id
      name
    }
    lookupJobStatus {
      id
      name
    }
    lookupCity {
      id
      name
    }
    lookupJobType {
      id
      name
    }
  }
`

const sortByAttribute = (items, attr, order) => {
  if (['name', 'company', 'category', 'address_line_1', 'city', 'job_types', 'color', 'supervisor', 'status'].includes(attr)) {
    return _.orderBy(items, [(o) => (o[attr] && o[attr].toLowerCase()) || o[attr]], [order])
  } else if (attr === 'job_id') {
    return _.orderBy(items, [(o) => parseInt(o[attr])], [order])
  } else {
    return _.orderBy(items, [(o) => o[attr]], [order])
  }
}

const headerStyle = {
  position: 'sticky',
  top: 110,
  zIndex: 5,
  backgroundColor: 'white',
  borderBottom: '1px gray solid',
  paddingTop: 40
}

const dailyUpdateButton = (recordId, categoryId) => {
  return categoryId === 1
    ? <ButtonLink to={`/daily-yardage/${recordId}`} icon='delta' style={{ margin: 0 }} />
    : categoryId === 2
      ? <ButtonLink to={`/daily-batches/${recordId}`} icon='delta' style={{ margin: 0 }} />
      : ''
}

const JobSearchResults = ({ items = [], isLoadingOrRefreshing, showCaret, isNotFullSize, ...props }) => {
  const navigate = useNavigate()

  const navigateToJob = (id, detailId) => navigate(`/job/${id}/edit/${detailId}`)
  const JobSearchResultCard = ({ job }) => (
    <Card key={`job-card-${job.id}`} className='mobile-search-result-card'>
      <div onClick={() => navigateToJob(job.job_id, job.job_detail_id)} style={{ overflowX: 'hidden' }}>
        <p>{job.job_id}{job.category && ' - '}{job.category}</p>
        <p>{job.name}</p>
        <p>{job.status}</p>
        <p>{job.company}</p>
        <p>{job.address_line_1}</p>
        <p>{job.city}</p>
        <p>{job.job_type}</p>
        <p>{job.color}</p>
        <p>{job.supervisor}</p>
        <p>Req Form? {job.request_form}</p>
        <p>{job.prep_date || '(No Prep Date)'}</p>
        <p>{job.app_or_finished_date || '(No App/Finished Date)'}</p>
      </div>
      <p style={{ textAlign: 'center', margin: 0 }}>{dailyUpdateButton(job.id, job.category_id)}</p>
    </Card>
  )

  const updateSortVal = props.updateSortVal
  const isEmpty = !items || items.length === 0
  const lastItemId = items && items.length
    ? items[items.length - 1].id || 'idless'
    : 'empty'

  return (
    <>
      {!isNotFullSize
        ? <><Table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={headerStyle} onClick={() => updateSortVal('job_id')}>Number <Icon icon={showCaret('job_id')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('name')}>Name <Icon icon={showCaret('name')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('company')}>Company <Icon icon={showCaret('company')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('category')}>Category <Icon icon={showCaret('category')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('address_line_1')}>Address <Icon icon={showCaret('address_line_1')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('city')}>City <Icon icon={showCaret('city')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('job_types')}>Job Types <Icon icon={showCaret('job_types')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('color')}>Color <Icon icon={showCaret('color')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('supervisor')}>Supervisor <Icon icon={showCaret('supervisor')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('request_form')}>Request Form? <Icon icon={showCaret('request_form')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('prep_date')}>Prep Date <Icon icon={showCaret('prep_date')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('app_or_finished_date')}>App Date / Finished Date  <Icon icon={showCaret('app_or_finished_date')} /></th>
              <th style={headerStyle} onClick={() => updateSortVal('status')}>Status <Icon icon={showCaret('status')} /></th>
              <th style={headerStyle}>Daily Updates</th>
            </tr>
          </thead>
          <tbody key={`tableBody-lastId-${lastItemId}`}>
            {items.sort(i => i.entered_date).map(({ id, ...item }) => (
              <tr key={id} onDoubleClick={() => navigateToJob(item.job_id, item.job_detail_id)}>
                <td>{item.job_id}</td>
                <td>{item.name}</td>
                <td>{item.company}</td>
                <td>{item.category}</td>
                <td>{item.address_line_1}</td>
                <td>{item.city}</td>
                <td>{item.job_types}</td>
                <td>{item.color}</td>
                <td>{item.supervisor}</td>
                <td style={{ textAlign: 'center' }}>{item.request_form}</td>
                <td>{item.prep_date}</td>
                <td>{item.app_or_finished_date}</td>
                <td>{item.status}</td>
                <td style={{ textAlign: 'center' }}>{dailyUpdateButton(id, item.category_id)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
          <div>{isLoadingOrRefreshing && <Spinner />}</div>
        </>
        : <Row>
          {items.map(job => (
            <Col xs={6} sm={4} md={2} key={`col-job-${job.id}`}>
              <JobSearchResultCard job={job} />
            </Col>
          ))
          }
          {isLoadingOrRefreshing &&
            <Col xs={12}>
              <div><Spinner /></div>
            </Col>
          }
        </Row>
      }
      {
        isEmpty && !isLoadingOrRefreshing &&
        <NonIdealState
          icon='search'
          title='No results found.'
        />
      }
    </>
  )
}

const formatJobTypesForDisplay = (jobTypes) => {
  return _.orderBy(jobTypes, 'name', 'asc').map(jt => jt.name).filter(n => n).join(', ')
}

const calculateLastSeen = (items, asc) => asc
  ? items.reduce((prev, curr) => (+prev.job_id > +curr.job_id) ? prev : curr, 0).job_id
  : items.reduce((prev, curr) => (+prev.job_id < +curr.job_id) ? prev : curr, 0).job_id


export default () => {
  const { isOnline } = useOnlineUtils()
  const {
    getMappedSearchCriteria, hasUpdatedCriteria, noMoreDataForCurrentCriteria,
    setNoMoreDataForCurrentCriteria, sethasUpdatedCriteria
  } = useJobSearchCriteria()

  const [sortVal, updateSortVal] = useState('job_id')
  const [order, updateOrder] = useState('desc')

  const updateSortValWithOrder = (s) => {
    const defaultSortOrder = 'asc'
    const newSortOrder = 'desc'
    if (s === sortVal) {
      updateOrder(order === defaultSortOrder ? newSortOrder : defaultSortOrder)
    }
    updateSortVal(s)
  }

  const showCaret = (colSortVal) => {
    if (sortVal === colSortVal) {
      if (order === 'desc') {
        return 'caret-down'
      } else {
        return 'caret-up'
      }
    } else {
      return ''
    }
  }

  const isNotFullSize = useMediaQuery({ query: '(max-width: 1255px)' })

  const { loading, error, data, refetch, networkStatus, fetchMore, useCached } = useOfflineQuery(QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      searchCriteria: getMappedSearchCriteria(false)
    }
  })

  const isLoadingOrRefreshing = loading || networkStatus === 2
  const supervisors = (data?.customer_contacts.map(s => ({ ...s, name: `${s.first_name} ${s.last_name}` }))) || []
  const homeowners = (data?.homeowners.map(h => ({ ...h, name: `${h.first_name} ${h.last_name}` }))) || []
  const jobSet = new Set(data && data.jobs)
  const jobUnset = [...jobSet]
  const jobs = data && data.jobs && jobUnset.map(j => {
    return {
      ...j,
      request_form: j.is_request_form ? 'Yes' : 'No',
      app_or_finished_date: j.app_date ?? j.finished_date,
      job_types: formatJobTypesForDisplay(j.job_types)
    }
  })

  const wrapperCardRef = useRef(null)
  useEffect(() => {
    const targetElement = wrapperCardRef.current
    const onScroll = () => {
      if (isLoadingOrRefreshing || (!hasUpdatedCriteria && noMoreDataForCurrentCriteria)) return
      const reachedBottom = isNotFullSize
        ? ((targetElement.scrollTop + 1) >= (targetElement.scrollHeight - targetElement.offsetHeight))
        : (Math.ceil(window.innerHeight + document.documentElement.scrollTop) >= document.documentElement.offsetHeight)

      if (reachedBottom) {
        if (hasUpdatedCriteria) {
          refetch({ searchCriteria: getMappedSearchCriteria(false) })
        } else {
          const last_seen = calculateLastSeen(jobs, getMappedSearchCriteria(false).asc)
          fetchMore({
            query: QUERY,
            variables: {
              searchCriteria: {
                ...getMappedSearchCriteria(false),
                last_seen: !isNaN(last_seen) ? +last_seen : null
              }
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return previousResult
              const newJobs = fetchMoreResult.jobs
              if (!newJobs || newJobs.length < 1) setNoMoreDataForCurrentCriteria(true)
              return {
                ...previousResult,
                jobs: [...previousResult.jobs, ...newJobs]
              }
            }
          })
        }
        sethasUpdatedCriteria(false)
      }
    }

    if (isNotFullSize) {
      targetElement.addEventListener('scroll', onScroll)
    } else {
      window.addEventListener('scroll', onScroll)
    }

    return () => isNotFullSize ? targetElement.removeEventListener('scroll', onScroll) : window.removeEventListener('scroll', onScroll)
  })


  if (error && !useCached) {
    return <ErrorMessage />
  }

  return (
    <>
      <CardWithBreadcrumb items={[{ text: 'Job Search', icon: 'briefcase' }]}>
        <ButtonLink to="/job/new" icon={'plus'} intent={'primary'}>New</ButtonLink>
      </CardWithBreadcrumb>

      {isOnline &&
        <JobFilters
          refetch={refetch}
          supervisors={supervisors}
          homeowners={homeowners}
          customers={data && data.customers}
          jobStatuses={data && data.lookupJobStatus}
          pebbleColors={data && data.lookupPebbleColor}
          jobCategories={data && data.lookupJobCategory}
          cities={data && data.lookupCity}
          jobTypes = {data && data.lookupJobType}
          isLoadingOrRefreshing={isLoadingOrRefreshing}
        />}

      <Card className={isNotFullSize ? 'mobile-search-results-wrapper-card' : ''}>
        <div ref={wrapperCardRef} className={isNotFullSize ? 'mobile-search-results-inner-scrollable-div' : ''}>
          <JobSearchResults
            isLoadingOrRefreshing={isLoadingOrRefreshing}
            items={(data && sortByAttribute(jobs, sortVal, order)) || []}
            updateSortVal={updateSortValWithOrder}
            showCaret={showCaret}
            isNotFullSize={isNotFullSize}
          />
        </div>
        {noMoreDataForCurrentCriteria &&
          <div style={{ textAlign: 'center' }}>
            No more results matching the current criteria.
          </div>
        }
      </Card>
    </>
  )
}
